/**
 * ============================================================================
 * ⭐ ONE CLICK, OR TWO? — the rail cards' click intent (F130, Decision 308)
 * ============================================================================
 *
 * > Pete, 2026-09-29: *"Think you need to single click waiting item on the driver,
 * > client carrier, consignee and the right side panels, was it put there?"* →
 * > driver card double-click *"Open the check-in"*; party card *"Its record in
 * > Settings"*.
 *
 * ⛔ WHY A WAIT AT ALL. A single click on these cards SCOPES the board (and a second
 * click on the chosen one clears it), so the first click of a double-click re-laid
 * the screen and the second undid it — the double-click never landed. The same
 * failure the calendar chips had (F121, Decision 303), solved the same way: a single
 * click waits `CLICK_DOUBLE_WAIT_MS` for a second one.
 *
 * ⭐ THE CONTRACT.
 *   · `click(key, single, double)` — the first click on `key` waits; a second click on
 *     the SAME key inside the wait runs `double` instead; otherwise `single` runs when
 *     the wait ends. A click on another key cancels the first one's wait.
 *   · `dbl(key, double)` — the browser's own double-click: drops a pending single and
 *     runs `double`, UNLESS the second click of the same double-click already did
 *     (so the check-in editor does not open twice).
 *   · Everything pending is cancelled when the component goes.
 */
import { useCallback, useEffect, useRef } from 'react';

/** The same wait as the calendar chips (`SCOPE_CLICK_DELAY_MS`). */
export const CLICK_DOUBLE_WAIT_MS = 250;

export interface ClickIntent {
    click: (key: string, single: () => void, double: () => void) => void;
    dbl: (key: string, double: () => void) => void;
    cancel: () => void;
}

export function useClickIntent(waitMs: number = CLICK_DOUBLE_WAIT_MS): ClickIntent {
    const pending = useRef<{ key: string; timer: number } | null>(null);
    const lastDouble = useRef<{ key: string; at: number } | null>(null);

    const cancel = useCallback(() => {
        const p = pending.current;
        if (p) window.clearTimeout(p.timer);
        pending.current = null;
    }, []);
    useEffect(() => cancel, [cancel]);

    const click = useCallback((key: string, single: () => void, double: () => void) => {
        if (pending.current?.key === key) {
            cancel();
            lastDouble.current = { key, at: Date.now() };
            double();
            return;
        }
        cancel();
        const timer = window.setTimeout(() => {
            pending.current = null;
            single();
        }, waitMs);
        pending.current = { key, timer };
    }, [cancel, waitMs]);

    const dbl = useCallback((key: string, double: () => void) => {
        cancel();
        const last = lastDouble.current;
        if (last && last.key === key && Date.now() - last.at < waitMs * 3) return;   // the click path already opened it
        lastDouble.current = { key, at: Date.now() };
        double();
    }, [cancel, waitMs]);

    return { click, dbl, cancel };
}
